/**
 * Pure row insert/delete helpers for Scoping's grouped tables — RACI and Resources
 * (backlog R008 / R009: "Group the table items as headings; provide editing features
 * to add / delete rows"). Extracted out of ProjectWorkspace.jsx so the insert-position
 * + phase-identity logic is unit-testable without mounting the full workspace page.
 *
 * Row shape: each row is a plain array positionally aligned to `header` (CSV-style),
 * e.g. header ["Phase ID","Phase title","Task title",...] / row ["1","Onboarding","Create project",...].
 * Grouping in the render layer keys off the "Phase title" column — see
 * ProjectWorkspace.jsx's `renderRaciFromTest` (RACI) and the `scopingTab === "resources"`
 * render block (Resources), both of which build a `phaseMap` keyed by that same column.
 */

function findColIdx(header, needle) {
  return (header || []).findIndex((h) => (h || "").toString().toLowerCase().includes(needle));
}

/**
 * Insert a new row for `phaseTitle` immediately after the last existing row belonging
 * to that phase, matched on the Phase Title column — the actual grouping key the
 * render layer groups by. Falls back to appending at the end when no row of that
 * phase exists yet (or the header has no Phase Title column). Carries the matched
 * group's Phase ID onto the new row so it doesn't render with a blank ID badge.
 *
 * Caller contract: pass the phase's TITLE, not its ID — passing the ID here is the
 * exact bug this helper was extracted to fix (see .claude/lessons — RACI's "+ Add
 * Task" was passing `phase.id` while this match logic keys on the title column, so
 * the row could never find its group and silently landed as an orphaned new group
 * at the end instead of inside the clicked phase).
 *
 * @param {Array<Array<string>>} rows
 * @param {Array<string>} header
 * @param {string} phaseTitle
 * @param {string} [taskLabel]
 * @returns {Array<Array<string>>} a new rows array — input `rows` is not mutated.
 */
export function insertRowInPhaseGroup(rows, header, phaseTitle, taskLabel = "New Task") {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeHeader = Array.isArray(header) ? header : [];
  const phaseTitleIdx = findColIdx(safeHeader, "phase title");
  const phaseIdIdx = findColIdx(safeHeader, "phase id");
  const taskColIdx = findColIdx(safeHeader, "task");

  let insertAt = safeRows.length;
  let matchedPhaseId = "";
  for (let i = safeRows.length - 1; i >= 0; i--) {
    const row = safeRows[i] || [];
    const val = phaseTitleIdx >= 0
      ? (row[phaseTitleIdx] || "").toString().trim()
      : phaseIdIdx >= 0
        ? (row[phaseIdIdx] || "").toString().trim()
        : "";
    if (val === phaseTitle) {
      insertAt = i + 1;
      matchedPhaseId = phaseIdIdx >= 0 ? row[phaseIdIdx] : "";
      break;
    }
  }

  const newRow = safeHeader.map(() => "");
  if (phaseTitleIdx >= 0) newRow[phaseTitleIdx] = phaseTitle;
  if (phaseIdIdx >= 0) newRow[phaseIdIdx] = matchedPhaseId;
  if (taskColIdx >= 0) newRow[taskColIdx] = taskLabel;

  const next = [...safeRows];
  next.splice(insertAt, 0, newRow);
  return next;
}

/**
 * Remove the row at `idx`. Pure — returns a new array, input not mutated.
 * @param {Array<Array<string>>} rows
 * @param {number} idx
 * @returns {Array<Array<string>>}
 */
export function removeRowAt(rows, idx) {
  const safeRows = Array.isArray(rows) ? rows : [];
  return safeRows.filter((_, i) => i !== idx);
}
